import { useEffect, useRef, useState } from "react";
import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api, { inr } from "@/lib/api";
import { toast } from "sonner";
import { LayoutDashboard, Package, ShoppingCart, Hammer, LogOut, Plus, Trash2, Edit2, Upload, X, Download, Upload as ImportIcon, MessageSquare, Eye, EyeOff } from "lucide-react";

function Sidebar() {
  const loc = useLocation();
  const { logout } = useAuth();
  const nav = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard },
    { to: "/admin/products", label: "Products", icon: Package },
    { to: "/admin/orders", label: "Orders", icon: ShoppingCart },
    { to: "/admin/custom", label: "Custom Requests", icon: Hammer },
    { to: "/admin/testimonials", label: "Testimonials", icon: MessageSquare },
  ];
  return (
    <aside className="w-64 min-h-screen bg-[#1C1917] text-stone-300 flex flex-col">
      <div className="p-6 border-b border-stone-800 flex items-center gap-3">
        <img src="/vf-logo.jpg" alt="Vardhman Furniture" className="h-10 w-10 object-cover rounded-full ring-1 ring-stone-700"/>
        <div>
          <p className="font-serif text-2xl text-white leading-none">Vardhman</p>
          <p className="text-xs tracking-[0.3em] uppercase text-[#A67B5B] mt-1">Admin</p>
        </div>
      </div>
      <nav className="flex-1 p-4 space-y-1">
        {nav.map((n) => {
          const Icon = n.icon;
          const active = loc.pathname === n.to;
          return (
            <Link key={n.to} to={n.to} data-testid={`admin-nav-${n.label.toLowerCase().split(' ')[0]}`} className={`flex items-center gap-3 px-3 py-2 text-sm ${active ? "bg-stone-800 text-white" : "hover:bg-stone-800/50"}`}>
              <Icon size={16} strokeWidth={1.5}/> {n.label}
            </Link>
          );
        })}
      </nav>
      <div className="p-4 border-t border-stone-800">
        <Link to="/" className="block text-xs uppercase tracking-widest text-stone-400 hover:text-white mb-2">← Back to store</Link>
        <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 text-xs uppercase tracking-widest text-stone-400 hover:text-white"><LogOut size={14}/> Logout</button>
      </div>
    </aside>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  const cards = stats ? [
    { l: "Revenue", v: inr(stats.revenue) },
    { l: "Total Orders", v: stats.total_orders },
    { l: "Paid Orders", v: stats.paid_orders },
    { l: "Products", v: stats.total_products },
    { l: "Custom Requests", v: stats.custom_requests },
    { l: "Customers", v: stats.customers },
  ] : [];
  return (
    <div>
      <h1 className="font-serif text-3xl">Dashboard</h1>
      <p className="text-sm text-stone-500 mt-1">Overview of your workshop.</p>
      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-4">
        {cards.map((c) => (
          <div key={c.l} data-testid={`stat-${c.l.toLowerCase().split(' ')[0]}`} className="bg-white border border-stone-200 p-6">
            <p className="text-xs tracking-widest uppercase text-stone-500">{c.l}</p>
            <p className="font-serif text-3xl mt-2">{c.v}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

const CATS = [
  { slug: "sofa", name: "Sofa" },
  { slug: "bedroom", name: "Bedroom" },
  { slug: "living-room", name: "Living Room" },
  { slug: "dining", name: "Dining" },
  { slug: "storage", name: "Storage" },
  { slug: "study-office", name: "Study & Office" },
  { slug: "bar", name: "Bar Furniture" },
];

const SUBCATS = {
  "sofa": ["Sofa", "Sofa cum Bed", "Center Table", "Corners", "Lounge Chair"],
  "bedroom": ["Bed", "Bed Side Table", "Wardrobe", "Dressing Table", "TV Cabinet", "Sofa cum Bed", "Side Table", "Wall Shelf"],
  "living-room": ["Coffee Table", "TV Cabinet", "Wall Shelf", "Book Shelf", "Sideboard", "Sofa", "Bench", "Shoe Rack Cabinet", "Lounge Chair", "Cabinet", "Temple", "Corners", "Crockery", "Kitchen Cabinet"],
  "dining": ["6 Seater Dining Table", "4 Seater Dining Table", "Dining Table", "Chair", "Bench"],
  "storage": ["Sideboard", "Cabinet", "Wardrobe", "Crockery", "Wall Shelf", "Book Shelf", "Temple", "Chester"],
  "study-office": ["Study Table", "Computer Table", "Chair", "Book Shelf", "Office Table", "Table", "Temple", "Wall Shelf"],
  "bar": ["Bar Cabinet", "Bar Chair", "Bar Trolly", "Wall Shelf", "Corners"],
};

const EMPTY = { name: "", slug: "", category: "sofa", subcategory: "", description: "", care_instructions: "", price: 0, images: [], stock: 10, material: "", dimensions: "", featured: false };

function Products() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const load = () => api.get("/products").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (p) => { setEditing(p.id); setForm({ ...EMPTY, ...p, images: p.images || [] }); setShowForm(true); };

  // Optimize the delivery URL from Cloudinary — adds f_auto,q_auto:best so
  // browsers get WebP/AVIF at near-lossless quality with no source change.
  const optimizeCloudinaryUrl = (url) => {
    if (typeof url !== "string" || !url.includes("/upload/")) return url;
    if (url.includes("/f_auto")) return url;
    return url.replace("/upload/", "/upload/f_auto,q_auto:best/");
  };

  // Ask the backend for a signed Cloudinary upload payload. Cached per admin
  // session — a fresh signature is only ~1s of work.
  const getCloudinarySignature = async () => {
    const r = await api.get("/admin/cloudinary-signature");
    return r.data;
  };

  // Upload files DIRECTLY to Cloudinary (bypasses our backend entirely so we
  // are not constrained by the Kubernetes ingress body-size limit that caps
  // ~1-5 MB uploads). Falls back to the backend proxy for smaller files if
  // Cloudinary is unreachable.
  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const uploaded = [];
    let sig = null;
    for (const f of files) {
      try {
        if (!sig) sig = await getCloudinarySignature();
        const fd = new FormData();
        fd.append("file", f);
        fd.append("api_key", sig.api_key);
        fd.append("timestamp", sig.timestamp);
        fd.append("signature", sig.signature);
        fd.append("folder", sig.folder);
        const res = await fetch(
          `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
          { method: "POST", body: fd }
        );
        if (!res.ok) {
          const errText = await res.text();
          throw new Error(errText || `HTTP ${res.status}`);
        }
        const data = await res.json();
        const url = optimizeCloudinaryUrl(data.secure_url || data.url);
        if (!url) throw new Error("No URL returned by Cloudinary");
        uploaded.push(url);
      } catch (err) {
        console.error("Cloudinary direct upload failed", err);
        toast.error(`Failed to upload ${f.name}: ${err.message || "network error"}`);
      }
    }
    if (uploaded.length) setForm((f) => ({ ...f, images: [...f.images, ...uploaded] }));
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeImage = (idx) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const slugify = (name) => {
    const base = (name || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    return base || "product";
  };

  const submit = async (e) => {
    e.preventDefault();
    // Auto-generate slug from name when creating a new product; keep existing slug when editing
    const slug = editing ? form.slug : `${slugify(form.name)}-${Math.random().toString(36).slice(2, 6)}`;
    const payload = { ...form, slug, price: Number(form.price), stock: Number(form.stock), images: form.images.filter(Boolean) };
    try {
      if (editing) await api.put(`/admin/products/${editing}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Saved");
      setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!confirm("Delete this product?")) return; await api.delete(`/admin/products/${id}`); toast.success("Deleted"); load(); };

  const importRef = useRef(null);
  const [ioBusy, setIoBusy] = useState(false);

  const exportCatalog = async () => {
    setIoBusy(true);
    try {
      const r = await api.get("/admin/products/export");
      const stamp = new Date().toISOString().split("T")[0];
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vardhman-catalog-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${r.data.counts.products} products with ${r.data.counts.images} images`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally { setIoBusy(false); }
  };

  const importCatalog = async (fileList) => {
    const f = fileList?.[0];
    if (!f) return;
    const mode = window.prompt("Import mode:\n  merge — skip products with same slug (safe, default)\n  replace — overwrite existing slugs\n  wipe — DELETE all existing products first (dangerous)\n\nType: merge / replace / wipe", "merge");
    if (!mode || !["merge", "replace", "wipe"].includes(mode)) {
      toast.error("Cancelled — invalid mode.");
      if (importRef.current) importRef.current.value = "";
      return;
    }
    setIoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/admin/products/import?mode=${mode}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported: ${r.data.inserted} new · ${r.data.updated} updated · ${r.data.skipped} skipped · ${r.data.images_restored} images restored`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setIoBusy(false);
      if (importRef.current) importRef.current.value = "";
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-3xl">Products</h1>
          <p className="text-sm text-stone-500 mt-1">{items.length} in catalog.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input ref={importRef} type="file" accept="application/json,.json" className="hidden" onChange={(e) => importCatalog(e.target.files)} data-testid="admin-import-input" />
          <button data-testid="admin-export-btn" onClick={exportCatalog} disabled={ioBusy} className="btn-secondary inline-flex items-center gap-2 disabled:opacity-60">
            <Download size={14}/> Export
          </button>
          <button data-testid="admin-import-btn" onClick={() => importRef.current?.click()} disabled={ioBusy} className="btn-secondary inline-flex items-center gap-2 disabled:opacity-60">
            <ImportIcon size={14}/> Import
          </button>
          <button data-testid="admin-add-product" onClick={openNew} className="btn-primary flex items-center gap-2"><Plus size={14}/> New Product</button>
        </div>
      </div>

      <div className="mt-4 text-xs text-stone-500 border-l-2 border-[#8B5A2B] bg-[#FAF6EF] pl-3 py-2 max-w-3xl">
        <strong className="text-[#8B5A2B]">Sync to your live site:</strong> Click <strong>Export</strong> here on preview to download a `.json` file that includes every product and image. Then open the admin panel on your <em>production</em> site and click <strong>Import</strong> to upload the same file — your entire catalog will be restored with photos intact.
      </div>

      <div className="mt-8 bg-white border border-stone-200">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-left text-xs uppercase tracking-widest">
            <tr><th className="p-3">Name</th><th className="p-3">Cat.</th><th className="p-3">Price</th><th className="p-3">Stock</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} data-testid={`admin-product-row-${p.slug}`} className="border-t border-stone-200">
                <td className="p-3"><div className="flex items-center gap-3"><img src={p.images?.[0]} className="w-10 h-10 object-cover" alt=""/><span>{p.name}</span></div></td>
                <td className="p-3">{p.category}</td>
                <td className="p-3">{inr(p.price)}</td>
                <td className="p-3">
                  <span data-testid={`admin-stock-${p.slug}`} className={`inline-block px-2 py-0.5 text-xs ${p.stock <= 0 ? "bg-red-100 text-red-700" : (p.stock <= 3 ? "bg-amber-100 text-amber-800" : "text-stone-700")}`}>
                    {p.stock <= 0 ? "Out of stock" : p.stock}
                  </span>
                </td>
                <td className="p-3 text-right">
                  <button data-testid={`admin-edit-${p.slug}`} onClick={() => openEdit(p)} className="text-stone-500 hover:text-stone-900 mr-3"><Edit2 size={14}/></button>
                  <button data-testid={`admin-delete-${p.slug}`} onClick={() => del(p.id)} className="text-stone-500 hover:text-red-700"><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-[#FAF8F5] p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="font-serif text-2xl mb-6">{editing ? "Edit Product" : "New Product"}</h2>
            <div className="grid grid-cols-2 gap-4">
              <input data-testid="pform-name" required placeholder="Name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})}/>
              <input data-testid="pform-price" required type="number" placeholder="Price (INR)" value={form.price} onChange={(e)=>setForm({...form,price:e.target.value})}/>
              <select data-testid="pform-category" value={form.category} onChange={(e)=>setForm({...form,category:e.target.value, subcategory: ""})}>
                {CATS.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
              <select data-testid="pform-subcategory" value={form.subcategory} onChange={(e)=>setForm({...form,subcategory:e.target.value})}>
                <option value="">Select Subcategory</option>
                {(SUBCATS[form.category] || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input type="number" placeholder="Stock" value={form.stock} onChange={(e)=>setForm({...form,stock:e.target.value})}/>
              <input placeholder="Material" value={form.material} onChange={(e)=>setForm({...form,material:e.target.value})}/>
              <input placeholder="Dimensions" value={form.dimensions} onChange={(e)=>setForm({...form,dimensions:e.target.value})}/>
            </div>
            <textarea className="mt-4" data-testid="pform-description" required rows={4} placeholder="Description — tell the story of this piece (wood, craft, dimensions in words)" value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})}/>
            <textarea className="mt-4" data-testid="pform-care" rows={3} placeholder="Care instructions — how the customer should clean, polish and maintain this piece (optional)" value={form.care_instructions || ""} onChange={(e)=>setForm({...form,care_instructions:e.target.value})}/>
            <p className="text-xs uppercase tracking-widest text-stone-500 mt-4 mb-2">Product Photos</p>
            <div className="border-2 border-dashed border-stone-300 p-4 bg-white">
              <input
                ref={fileRef}
                data-testid="pform-file-input"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                multiple
                onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
                className="hidden"
              />
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <button
                  type="button"
                  data-testid="pform-upload-btn"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="btn-secondary inline-flex items-center gap-2 disabled:opacity-60"
                >
                  <Upload size={14}/> {uploading ? "Uploading…" : "Choose from Gallery"}
                </button>
                <p className="text-xs text-stone-500">Pick one or more images from your phone or laptop. JPG / PNG / WEBP up to 8 MB each.</p>
              </div>
              {form.images.length > 0 && (
                <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {form.images.map((u, i) => (
                    <div key={i} className="relative group aspect-square bg-stone-100 overflow-hidden">
                      <img src={u} alt={`Image ${i+1}`} className="w-full h-full object-cover"/>
                      <button
                        type="button"
                        data-testid={`pform-remove-image-${i}`}
                        onClick={() => removeImage(i)}
                        aria-label="Remove image"
                        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      ><X size={12}/></button>
                      {i === 0 && <span className="absolute bottom-1 left-1 bg-[#8B5A2B] text-white text-[10px] uppercase tracking-widest px-1.5 py-0.5">Cover</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <label className="flex items-center gap-2 mt-4 text-sm"><input type="checkbox" className="w-4 h-4" checked={form.featured} onChange={(e)=>setForm({...form,featured:e.target.checked})}/> Featured on home page</label>
            <div className="mt-6 flex gap-3 justify-end">
              <button type="button" onClick={()=>setShowForm(false)} className="btn-secondary">Cancel</button>
              <button data-testid="pform-save" type="submit" className="btn-primary">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState([]);
  const [detailOrder, setDetailOrder] = useState(null);
  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    await api.put(`/admin/orders/${id}/status`, { status });
    toast.success("Updated — customer notified by email");
    load();
    if (detailOrder?.id === id) setDetailOrder({ ...detailOrder, status });
  };
  const resend = async (id) => {
    try {
      await api.post(`/admin/orders/${id}/resend-notification`);
      toast.success("Notification email re-sent");
    } catch { toast.error("Could not resend"); }
  };
  return (
    <div>
      <h1 className="font-serif text-3xl">Orders</h1>
      <div className="mt-6 bg-white border border-stone-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-left text-xs uppercase tracking-widest">
            <tr><th className="p-3">ID</th><th className="p-3">Customer</th><th className="p-3">Items</th><th className="p-3">Payment</th><th className="p-3">Total</th><th className="p-3">Status</th><th className="p-3">Set</th><th className="p-3">View</th></tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-stone-200 align-top">
                <td className="p-3 font-mono text-xs">{o.id.slice(0,8)}</td>
                <td className="p-3">
                  <div>{(o.shipping_address || {}).name || o.user_email}</div>
                  <div className="text-[10px] text-stone-500">{o.user_email}</div>
                  {(o.shipping_address || {}).phone && <div className="text-[10px] text-stone-500">{(o.shipping_address || {}).phone}</div>}
                </td>
                <td className="p-3">{o.items.length}</td>
                <td className="p-3 text-xs">
                  <span className={`px-2 py-1 uppercase tracking-widest ${o.payment_method === "cod" ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-800"}`}>
                    {o.payment_method === "cod" ? "COD" : "Online"}
                  </span>
                  {(o.delivery_charge || o.cod_fee) ? (
                    <div className="text-[10px] text-stone-500 mt-1">
                      Delivery: {inr(o.delivery_charge || o.cod_fee)}{o.delivery_distance_km ? ` · ${o.delivery_distance_km} km` : ""}
                    </div>
                  ) : null}
                </td>
                <td className="p-3">{inr(o.total)}{o.discount ? <div className="text-[10px] text-emerald-700">-{inr(o.discount)} first order</div> : null}</td>
                <td className="p-3 uppercase tracking-widest text-xs text-[#8B5A2B]">{o.status}</td>
                <td className="p-3">
                  <select value={o.status} onChange={(e)=>setStatus(o.id, e.target.value)} className="text-xs">
                    {["pending","cod_pending","paid","shipped","delivered","cancelled"].map((s)=> <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="p-3">
                  <button
                    data-testid={`admin-order-view-${o.id}`}
                    onClick={() => setDetailOrder(o)}
                    className="text-xs tracking-widest uppercase text-[#8B5A2B] hover:underline"
                  >Details</button>
                </td>
              </tr>
            ))}
            {orders.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-stone-500">No orders yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {detailOrder && <OrderDetailModal order={detailOrder} onClose={() => setDetailOrder(null)} onStatusChange={setStatus} onResend={resend}/>}
    </div>
  );
}

function OrderDetailModal({ order, onClose, onStatusChange, onResend }) {
  const addr = order.shipping_address || {};
  const custPhone = (addr.phone || "").replace(/\D/g, "");
  const waNumber = custPhone.length === 10 ? `91${custPhone}` : custPhone;

  const confirmMsg = `Hi ${addr.name || "there"}, this is Vardhman Furniture. Your order ${order.id.slice(0, 8)} is confirmed. Total ${inr(order.total)} (${order.payment_method === "cod" ? "Cash on Delivery" : "Paid Online"}). Our team will call you shortly to schedule delivery. Thank you!`;
  const shipMsg = `Hi ${addr.name || "there"}, your Vardhman order ${order.id.slice(0, 8)} has been shipped. It will reach you at ${addr.city || ""} - ${addr.pincode || ""}. Track anytime at our website "Track Order" section using your Order ID and last 4 digits of your phone. Thank you!`;
  const message = order.status === "shipped" ? shipMsg : confirmMsg;
  const waHref = waNumber ? `https://wa.me/${waNumber}?text=${encodeURIComponent(message)}` : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" onClick={onClose}>
      <div
        data-testid="admin-order-detail-modal"
        onClick={(e) => e.stopPropagation()}
        className="bg-white max-w-3xl mx-auto my-8 shadow-2xl"
      >
        <div className="flex items-start justify-between p-6 border-b border-stone-200">
          <div>
            <p className="text-[10px] tracking-widest uppercase text-stone-500">Order</p>
            <p className="font-mono text-sm mt-1">{order.id}</p>
            <p className="text-xs text-stone-500 mt-1">Placed {new Date(order.created_at).toLocaleString("en-IN")}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded"><X size={18}/></button>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-[10px] tracking-widest uppercase text-stone-500">Customer</p>
            <p className="mt-2 font-medium">{addr.name || order.user_email}</p>
            <p className="text-sm text-stone-600">{order.user_email}</p>
            {addr.phone && <p className="text-sm text-stone-600">{addr.phone}</p>}
          </div>
          <div>
            <p className="text-[10px] tracking-widest uppercase text-stone-500">Delivery Address</p>
            <p className="mt-2 text-sm">{addr.line1 || "—"}</p>
            <p className="text-sm text-stone-600">{addr.city || ""}, {addr.state || ""} — {addr.pincode || ""}</p>
            {order.delivery_distance_km && <p className="text-xs text-stone-500 mt-1">Distance from shop: {order.delivery_distance_km} km</p>}
          </div>
        </div>

        <div className="px-6 pb-2">
          <p className="text-[10px] tracking-widest uppercase text-stone-500 mb-3">Items ({order.items.length})</p>
          <div className="border border-stone-200">
            {order.items.map((it, i) => (
              <div key={i} className={`flex items-center gap-3 p-3 ${i > 0 ? "border-t border-stone-100" : ""}`}>
                {it.image && <img src={it.image} alt={it.name} className="w-14 h-14 object-cover bg-stone-100"/>}
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{it.name}</p>
                  <p className="text-xs text-stone-500">Qty {it.quantity} × {inr(it.price)}</p>
                </div>
                <p className="text-sm">{inr(it.price * it.quantity)}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-stone-200 space-y-1 text-sm">
          <div className="flex justify-between text-stone-600"><span>Subtotal</span><span>{inr(order.subtotal || 0)}</span></div>
          {order.discount ? <div className="flex justify-between text-emerald-700"><span>First-order discount ({order.discount_pct || 5}%)</span><span>- {inr(order.discount)}</span></div> : null}
          <div className="flex justify-between text-stone-600"><span>Delivery {order.delivery_distance_km ? `(${order.delivery_distance_km} km)` : ""}</span><span>{inr(order.delivery_charge || order.cod_fee || 0)}</span></div>
          <div className="flex justify-between font-serif text-lg pt-2 border-t border-stone-200 mt-2"><span>Total</span><span>{inr(order.total || 0)}</span></div>
          <div className="flex justify-between text-xs text-stone-500 pt-1"><span>Payment</span><span className="uppercase tracking-widest">{order.payment_method === "cod" ? "Cash on Delivery" : "Online (Razorpay)"}</span></div>
          <div className="flex justify-between text-xs text-stone-500"><span>Current Status</span><span className="uppercase tracking-widest text-[#8B5A2B]">{order.status}</span></div>
        </div>

        <div className="p-6 border-t border-stone-200 bg-[#FAF6EF] space-y-4">
          <div>
            <p className="text-[10px] tracking-widest uppercase text-stone-500 mb-2">Confirm & Notify Customer</p>
            <div className="flex flex-wrap gap-2">
              {order.status !== "paid" && (
                <button
                  data-testid="admin-order-confirm-paid"
                  onClick={() => onStatusChange(order.id, "paid")}
                  className="btn-primary text-sm"
                >Mark as Confirmed / Paid → send email</button>
              )}
              {order.status === "paid" && (
                <button
                  data-testid="admin-order-mark-shipped"
                  onClick={() => onStatusChange(order.id, "shipped")}
                  className="btn-primary text-sm"
                >Mark as Shipped → send tracking email</button>
              )}
              <button
                data-testid="admin-order-resend"
                onClick={() => onResend(order.id)}
                className="btn-secondary text-sm"
              >Resend email</button>
              {waHref && (
                <a
                  data-testid="admin-order-whatsapp"
                  href={waHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#25D366] text-white hover:opacity-90"
                >WhatsApp customer</a>
              )}
            </div>
            <p className="mt-2 text-[11px] text-stone-500">Email is sent automatically when you change status to Paid or Shipped. WhatsApp opens with a pre-filled message.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function CustomRequests() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/custom-orders").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { await api.put(`/admin/custom-orders/${id}/status`, { status }); toast.success("Updated"); load(); };
  return (
    <div>
      <h1 className="font-serif text-3xl">Custom Requests</h1>
      <div className="mt-6 space-y-4">
        {items.length === 0 && <p className="text-stone-500">No requests yet.</p>}
        {items.map((r) => (
          <div key={r.id} data-testid={`admin-custom-${r.id}`} className="bg-white border border-stone-200 p-6">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-serif text-xl">{r.furniture_type}</h3>
                <p className="text-sm text-stone-500">{r.name} · {r.email} · {r.phone}</p>
              </div>
              <select defaultValue={r.status} onChange={(e)=>setStatus(r.id, e.target.value)} className="text-xs max-w-[140px]">
                {["new","in_review","quoted","in_production","delivered","closed"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <p className="mt-4 text-stone-700">{r.description}</p>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-stone-600">
              <div><span className="uppercase tracking-widest text-stone-400">Wood</span><p>{r.wood_type || "—"}</p></div>
              <div><span className="uppercase tracking-widest text-stone-400">Dimensions</span><p>{r.dimensions || "—"}</p></div>
              <div><span className="uppercase tracking-widest text-stone-400">Budget</span><p>{r.budget || "—"}</p></div>
              <div><span className="uppercase tracking-widest text-stone-400">When</span><p>{new Date(r.created_at).toLocaleDateString()}</p></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TestimonialsAdmin() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const toggle = async (t) => {
    try {
      await api.put(`/admin/testimonials/${t.id}`, { approved: !t.approved });
      toast.success(t.approved ? "Hidden from site" : "Published to site");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const del = async (t) => {
    if (!window.confirm("Delete this testimonial permanently?")) return;
    try { await api.delete(`/admin/testimonials/${t.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <h1 className="font-serif text-4xl">Customer Testimonials</h1>
      <p className="mt-2 text-sm text-stone-500">Reviews about the shopping experience shown on the homepage. Hide or delete anything that shouldn&apos;t be public.</p>
      <div className="mt-8 grid md:grid-cols-2 gap-4">
        {items.length === 0 && <p className="text-sm text-stone-500 italic">No testimonials yet.</p>}
        {items.map((t) => (
          <div key={t.id} data-testid={`admin-testimonial-${t.id}`} className={`p-5 border ${t.approved ? "border-stone-200 bg-white" : "border-stone-300 bg-stone-100 opacity-70"}`}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-medium text-stone-800">{t.user_name}{t.city ? ` · ${t.city}` : ""}</p>
                <p className="text-xs text-stone-500">{new Date(t.created_at).toLocaleDateString()} · Rating {t.rating}/5</p>
              </div>
              <span className={`text-[10px] tracking-widest uppercase ${t.approved ? "text-[#5c7a3a]" : "text-stone-500"}`}>
                {t.approved ? "Live" : "Hidden"}
              </span>
            </div>
            <p className="mt-3 text-sm text-stone-700 leading-relaxed">{t.comment}</p>
            <div className="mt-4 flex gap-2">
              <button
                data-testid={`admin-testimonial-toggle-${t.id}`}
                onClick={() => toggle(t)}
                className="text-xs tracking-widest uppercase text-stone-600 hover:text-stone-900 flex items-center gap-1"
              >
                {t.approved ? <><EyeOff size={12}/> Hide</> : <><Eye size={12}/> Publish</>}
              </button>
              <button
                data-testid={`admin-testimonial-delete-${t.id}`}
                onClick={() => del(t)}
                className="text-xs tracking-widest uppercase text-red-600 hover:text-red-800 flex items-center gap-1"
              >
                <Trash2 size={12}/> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminApp() {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-12">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

  return (
    <div className="flex bg-[#FAF8F5]" data-testid="admin-app">
      <Sidebar />
      <div className="flex-1 p-10 min-h-screen">
        <Routes>
          <Route path="/" element={<Dashboard/>} />
          <Route path="/products" element={<Products/>} />
          <Route path="/orders" element={<Orders/>} />
          <Route path="/custom" element={<CustomRequests/>} />
          <Route path="/testimonials" element={<TestimonialsAdmin/>} />
        </Routes>
      </div>
    </div>
  );
}
